import { Directive, ElementRef, HostListener, Input, OnDestroy, ViewContainerRef } from '@angular/core';
import { AutocompleteComponent } from '../components/autocomplete/autocomplete.component';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { Subscription } from 'rxjs';
import { TemplatePortal } from '@angular/cdk/portal';

@Directive({
  selector: '[appAutocomplete]',
})
export class AutocompleteTriggerDirective implements OnDestroy {
  @Input('appAutocomplete') panel!: AutocompleteComponent;

  private overlayRef?: OverlayRef;
  private subs = new Subscription();

  constructor(private el: ElementRef<HTMLInputElement>, private overlay: Overlay, private vcr: ViewContainerRef) {}

  // Push filter & open/close
  @HostListener('focus') onFocus() {
    this.pushFilter();
    this.updateAndOpen(true);
  }

  @HostListener('input') onInput() {
    this.pushFilter();
    this.updateAndOpen(false);
  }

  @HostListener('blur') onBlur() {
    //setTimeout(() => this.close(), 100);
  }

  // Keyboard navigation
  @HostListener('keydown', ['$event'])
  onKeydown(ev: KeyboardEvent) {
    if (!this.overlayRef?.hasAttached()) {
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
        this.open();
        ev.preventDefault();
      } else {
        return;
      }
    }

    switch (ev.key) {
      case 'ArrowDown':
        this.panel.moveActive(1);
        ev.preventDefault();
        break;
      case 'ArrowUp':
        this.panel.moveActive(-1);
        ev.preventDefault();
        break;
      case 'Home':
        this.panel.setActiveFirstOption();
        ev.preventDefault();
        break;
      case 'End':
        this.panel.setActiveLastOption();
        ev.preventDefault();
        break;
      case 'Enter':
        this.panel.selectActiveOption();
        ev.preventDefault();
        break;
      case 'Escape':
        this.close();
        ev.preventDefault();
        break;
    }
  }

  private pushFilter() {
    this.panel?.setFilterText(this.el.nativeElement.value ?? '');
  }

  private updateAndOpen(resetActiveOnOpen: boolean) {
    if (!this.overlayRef?.hasAttached()) {
      this.open(resetActiveOnOpen);
    }
  }

  private open(resetActiveOnOpen = true) {
    if (this.overlayRef?.hasAttached()) return;

    const strategy = this.overlay
      .position()
      .flexibleConnectedTo(this.el)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
      ])
      .withFlexibleDimensions(true)
      .withPush(true);

    this.overlayRef ??= this.overlay.create({
      positionStrategy: strategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      width: this.el.nativeElement.offsetWidth,
    });

    this.panel.markAsInitialized();

    // Attach panel
    const portal = new TemplatePortal(this.panel.panelTpl, this.vcr);
    this.overlayRef.attach(portal);

    // ARIA on the input
    const input = this.el.nativeElement;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'true');
    input.setAttribute('aria-controls', this.panel.id);

    // Keep activedescendant up to date
    this.subs.add(
      this.panel.activeDescendantIdChange.subscribe((id) => {
        if (id) input.setAttribute('aria-activedescendant', id);
        else input.removeAttribute('aria-activedescendant');
      })
    );

    // Option selection -> write to input & close
    this.subs.add(
      this.panel.optionSelected.subscribe((opt) => {
        const input = this.el.nativeElement;
        input.value = String(opt.value?.trim() ?? input.value);
        input.dispatchEvent(new Event('input'));
        this.close();
      })
    );

    this.subs.add(this.overlayRef.backdropClick().subscribe(() => this.close()));
    this.subs.add(this.overlayRef.detachments().subscribe(() => this.cleanup()));

    if (resetActiveOnOpen) this.panel.setActiveFirstOption();
  }

  private close() {
    if (!this.overlayRef?.hasAttached()) return;
    this.el.nativeElement.setAttribute('aria-expanded', 'false');
    this.overlayRef.detach();
    // clear activedescendant when closed
    this.el.nativeElement.removeAttribute('aria-activedescendant');
  }

  private cleanup() {
    this.subs.unsubscribe();
    this.subs = new Subscription();
  }

  ngOnDestroy() {
    this.overlayRef?.dispose();
    this.subs.unsubscribe();
  }
}
